/**
 * AssigneePicker · Sprint 13 (S13.1)
 *
 * Searchable combobox für "Wer übernimmt?"-Feld in Capture und LeadDetail.
 * Ersetzt das simple <select>/<input>-Pattern durch typeahead-Suche:
 *
 * 1. Lädt User-Liste 1× via GET /api/users (operator_admin/dispatcher sehen
 *    alle Tenant-User; employee sieht sich selbst — Backend filtert).
 * 2. User tippt Name → liste wird live gefiltert (case-insensitive substring).
 * 3. Klick auf Item → setzt internen Wert auf user.id (UUID, Backend-FK-Format).
 * 4. Anzeigewert ist user.full_name; clear-Button setzt Wert zurück.
 *
 * Props.value = UUID-String (oder leer); Props.onChange übergibt UUID-String
 * (oder leer) — kompatibel mit existierendem `uebernehmender_id`-Schema.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { api } from '../lib/api';

interface AssigneeUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
  inputId?: string;
  placeholder?: string;
}

// Module-Cache — verhindert N-fach-Fetch bei mehrfachen Pickern auf 1 Seite.
// Sprint 17 (S17.1) · Cache-TTL 60s, damit nach Impersonate / User-Anlage
// die Liste binnen einer Minute frisch ist (vorher hing sie ewig).
let usersCache: AssigneeUser[] | null = null;
let usersCacheAt = 0;
let usersInflight: Promise<AssigneeUser[]> | null = null;
const USERS_CACHE_TTL_MS = 60_000;

export function invalidateAssigneeCache(): void {
  usersCache = null;
  usersCacheAt = 0;
}

async function loadUsers(): Promise<AssigneeUser[]> {
  if (usersCache && Date.now() - usersCacheAt < USERS_CACHE_TTL_MS) return usersCache;
  if (usersInflight) return usersInflight;
  usersInflight = api<AssigneeUser[]>('/api/users')
    .then((list) => {
      // Inactive User werden nicht angezeigt — sie können keinen Lead übernehmen.
      const filtered = list.filter((u) => u.is_active);
      usersCache = filtered;
      usersCacheAt = Date.now();
      return filtered;
    })
    .finally(() => {
      usersInflight = null;
    });
  return usersInflight;
}

export function AssigneePicker({ value, onChange, inputId, placeholder }: Props) {
  const [users, setUsers] = useState<AssigneeUser[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Initial-Load
  useEffect(() => {
    let cancel = false;
    loadUsers()
      .then((list) => {
        if (!cancel) setUsers(list);
      })
      .catch((err: { detail?: string }) => {
        if (!cancel) setError(err.detail ?? 'User-Liste konnte nicht geladen werden');
      });
    return () => {
      cancel = true;
    };
  }, []);

  // Selected-User → query syncen wenn extern gesetzt
  useEffect(() => {
    if (!value) {
      setQuery('');
      return;
    }
    const found = users.find((u) => u.id === value);
    if (found) setQuery(found.full_name);
  }, [value, users]);

  // Click-Outside → close
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 20);
    return users
      .filter(
        (u) =>
          u.full_name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      )
      .slice(0, 20);
  }, [users, query]);

  function selectUser(u: AssigneeUser) {
    onChange(u.id);
    setQuery(u.full_name);
    setOpen(false);
  }

  function clearSelection() {
    onChange('');
    setQuery('');
    setOpen(true);
  }

  return (
    <div className="assignee-picker" ref={wrapRef}>
      <div className="assignee-picker__input-wrap">
        <input
          id={inputId}
          className="auth-input assignee-picker__input"
          type="text"
          autoComplete="off"
          placeholder={placeholder ?? 'Name suchen…'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // Wenn user neu tippt, alte Selection invalidieren — sonst stimmt
            // angezeigter Name nicht mit gespeicherter ID überein.
            if (value) onChange('');
          }}
          onFocus={() => setOpen(true)}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-haspopup="listbox"
        />
        {value ? (
          <button
            type="button"
            className="assignee-picker__icon-btn"
            onClick={clearSelection}
            aria-label="Auswahl zurücksetzen"
          >
            <X size={16} />
          </button>
        ) : (
          <button
            type="button"
            className="assignee-picker__icon-btn"
            onClick={() => setOpen((v) => !v)}
            aria-label="Liste öffnen"
            tabIndex={-1}
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>
      {open && filtered.length > 0 ? (
        <ul className="assignee-picker__list" role="listbox">
          {filtered.map((u) => (
            <li
              key={u.id}
              className={`assignee-picker__item ${u.id === value ? 'assignee-picker__item--active' : ''}`}
              role="option"
              aria-selected={u.id === value}
            >
              <button
                type="button"
                className="assignee-picker__item-btn"
                onMouseDown={(e) => {
                  // mousedown statt click damit input-blur nicht vorher feuert
                  e.preventDefault();
                  selectUser(u);
                }}
              >
                <span className="assignee-picker__name">{u.full_name}</span>
                <span className="assignee-picker__role">{u.role}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open && filtered.length === 0 && query.trim() ? (
        <div className="assignee-picker__empty">Kein Treffer für „{query}“</div>
      ) : null}
      {open && users.length === 0 && !query.trim() && !error ? (
        <div className="assignee-picker__empty">Lade Nutzerliste…</div>
      ) : null}
      {error ? <div className="form-field__hint">{error}</div> : null}
    </div>
  );
}
